import { useState, useMemo, useEffect } from "react";
import { GitCompare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Voice } from '@/types/voice/voice';
import { VoiceFavorite } from '@/types/voice/favorites';
import { SetupAnalysisResult } from '@/types/agent/setup';
import { toast } from "sonner";
import { useToggleFavorite } from "@/hooks/voice/useVoiceFavorites";
import { useSharedVoices, useVoiceCategories } from "@/hooks/voice/useVoices";
import { VoiceQuickTestSheet } from '../VoiceQuickTestSheet';
import { VoiceComparisonPanel } from '../VoiceComparisonPanel';
import { VoiceDropdown } from './VoiceDropdown';
import { VoiceBrowserModal } from './VoiceBrowser';
import { VoiceService } from '@/services/integrations/elevenlabs/VoiceService';

interface EnhancedVoiceSelectorV2Props {
  value: string;
  onChange: (voiceId: string) => void;
  voices?: Voice[];
  voicesLoading: boolean;
  voicesTotalCount?: number;
  voicesHasMore?: boolean;
  onLoadMoreVoices?: () => void;
  currentVoice?: Voice;
  onRefetchVoices?: () => void;
  className?: string;
  defaultTestText?: string;
  genieConfig?: Partial<SetupAnalysisResult>;
  favorites?: VoiceFavorite[];
  favoritesLoading?: boolean;
  /** When true, restricts the selector to the `voices` prop (preselected + favorites)
   * and hides the "All Voices" browse tab that fetches the full catalog. */
  restrictToVoices?: boolean;
  /** When true, the "All Voices" modal allows selection without creating a library entry. */
  allowLibraryAdd?: boolean;
  /** When true, hides the play preview button next to the dropdown. */
  hidePlayButton?: boolean;
}

interface AllVoicesFilter {
  category: string;
  accent: string;
  language: string;
  age: string;
  gender: string;
  search: string;
}

export const EnhancedVoiceSelectorV2 = ({
  value,
  onChange,
  voices,
  voicesLoading,
  voicesTotalCount,
  voicesHasMore,
  onLoadMoreVoices,
  currentVoice,
  className,
  defaultTestText,
  favorites = [],
  restrictToVoices = false,
  allowLibraryAdd = true,
  hidePlayButton = false,
}: EnhancedVoiceSelectorV2Props) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [playingVoiceId, setPlayingVoiceId] = useState<string | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [testSheetOpen, setTestSheetOpen] = useState(false);
  const [testingVoice, setTestingVoice] = useState<Voice | null>(null);
  const [comparisonPanelOpen, setComparisonPanelOpen] = useState(false);
  const [comparisonVoices, setComparisonVoices] = useState<Voice[]>([]);
  const [allVoicesPage, setAllVoicesPage] = useState<number>(0);
  const [accumulatedAllVoices, setAccumulatedAllVoices] = useState<Voice[]>([]);
  const [prevAllVoicesFilters, setPrevAllVoicesFilters] = useState<AllVoicesFilter | undefined>(undefined);
  const [allVoicesFilters, setAllVoicesFilters] = useState<AllVoicesFilter>({
    category: 'all',
    accent: 'all',
    language: 'all',
    age: 'all',
    gender: 'all',
    search: '',
  });
  const [selectedVoiceSnapshot, setSelectedVoiceSnapshot] = useState<Voice | undefined>(currentVoice);

  useEffect(() => {
    setSelectedVoiceSnapshot(currentVoice);
  }, [currentVoice]);

  const { mutate: toggleFavorite } = useToggleFavorite();

  // Fetch all voices with pagination and server-side filtering
  const { data: allVoicesData, isLoading: allVoicesLoading } = useSharedVoices({
    enabled: !restrictToVoices,
    page: allVoicesPage,
    pageSize: 100,
    search: allVoicesFilters.search || undefined,
    category: allVoicesFilters.category !== 'all' ? allVoicesFilters.category : undefined,
    accent: allVoicesFilters.accent !== 'all' ? allVoicesFilters.accent : undefined,
    language: allVoicesFilters.language !== 'all' ? allVoicesFilters.language : undefined,
    age: allVoicesFilters.age !== 'all' ? allVoicesFilters.age : undefined,
    gender: allVoicesFilters.gender !== 'all' ? allVoicesFilters.gender : undefined,
  });

  // Get available filter categories
  const categories = useVoiceCategories();

  // Accumulate voices from paginated results for 'all' tab
  useEffect(() => {
    if (allVoicesData?.voices) {
      const filtersChanged = JSON.stringify(prevAllVoicesFilters) !== JSON.stringify(allVoicesFilters);
      if (filtersChanged || allVoicesPage === 0) {
        setAccumulatedAllVoices(allVoicesData.voices);
        setPrevAllVoicesFilters(allVoicesFilters);
      } else {
        setAccumulatedAllVoices(prev => {
          const voiceIds = new Set(prev.map(v => v.id));
          const newVoices = allVoicesData.voices.filter(v => !voiceIds.has(v.id));
          return [...prev, ...newVoices];
        });
      }
    }
  }, [allVoicesData, allVoicesPage, allVoicesFilters]);

  // Convert favorites to Set for quick lookup
  const favoriteVoiceIds = useMemo(
    () => new Set(favorites.map(f => f.voice_id)),
    [favorites]
  );

  // Get favorite voices list sorted alphabetically by name
  const favoriteVoicesList = useMemo(() => {
    return favorites
      .map(f => f.voice_data)
      .filter(Boolean)
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [favorites]);

  // Get recommended voices list - prioritize featured voices, then return top 50, sorted alphabetically.
  // When restricted, `voices` is already the small preselected+favorites list, so use it as-is.
  const recommendedVoicesList = useMemo(() => {
    if (!voices || voices.length === 0) return [];

    if (restrictToVoices) {
      return [...voices].sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    }

    // Filter for featured voices first, then fallback to all voices
    const featured = voices.filter(voice => voice.featured);
    const voiceList = featured.length > 0 ? featured.slice(0, 50) : voices.slice(0, 50);

    // Sort alphabetically by name
    return voiceList.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [voices, restrictToVoices]);

  // Get unique filter values from all accumulated voices
  const filterOptions = useMemo(() => {
    const allVoices = accumulatedAllVoices.length > 0 ? accumulatedAllVoices : voices || [];

    const accents = new Set<string>();
    const genders = new Set<string>();
    const ages = new Set<string>();
    const languages = new Set<string>();

    allVoices.forEach(voice => {
      if (voice.labels?.accent) accents.add(voice.labels.accent);
      if (voice.labels?.gender) genders.add(voice.labels.gender);
      if (voice.labels?.age) ages.add(voice.labels.age);
      if (voice.labels?.language) languages.add(voice.labels.language);
    });

    return {
      accents: Array.from(accents).sort(),
      genders: Array.from(genders).sort(),
      ages: Array.from(ages).sort(),
      languages: Array.from(languages).sort(),
      categories: categories.sort(),
    };
  }, [accumulatedAllVoices, voices, categories]);

  const handleVoiceSelect = (voiceId: string) => {
    onChange(voiceId);
    setDropdownOpen(false);

    const selectedVoice = voices?.find(v => v.id === voiceId);
    if (selectedVoice) {
      toast.success(`Voice selected: ${selectedVoice.name}`);
    }
  };

  const playVoicePreview = async (voice: Voice, e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (!voice.preview_url) return;

    try {
      setPlayingVoiceId(voice.id);
      const audio = new Audio(voice.preview_url);
      audio.onended = () => setPlayingVoiceId(null);
      audio.onerror = () => setPlayingVoiceId(null);
      await audio.play();
    } catch (error) {
      console.error("Failed to play voice preview:", error);
      setPlayingVoiceId(null);
    }
  };

  const handleToggleFavorite = (voice: Voice, e?: React.MouseEvent, fromAllVoicesTab: boolean = false) => {
    e?.stopPropagation();
    const isFavorited = favoriteVoiceIds.has(voice.id);
    toggleFavorite({
      voiceId: voice.id,
      voice,
      isFavorited,
      isSharedVoice: fromAllVoicesTab
    });
  };

  const handleTestVoice = (voice: Voice, e?: React.MouseEvent) => {
    e?.stopPropagation();
    setTestingVoice(voice);
    setTestSheetOpen(true);
  };

  const handleAddVoice = async (voice: Voice): Promise<Voice | null> => {
    if (!allowLibraryAdd) {
      return voice;
    }

    try {
      const voiceService = VoiceService.getInstance();
      const addedVoice = await voiceService.addVoice(voice);
      if (addedVoice) {
        toast.success(`Voice added to library: ${addedVoice.name}`);
      }
      return addedVoice;
    } catch (error) {
      console.error("Failed to add voice:", error);
      toast.error("Failed to add voice to library");
      return null;
    }
  };

  const handleRemoveFromComparison = (voiceId: string) => {
    setComparisonVoices(comparisonVoices.filter(v => v.id !== voiceId));
  };

  return (
    <>
      <VoiceDropdown
        value={value}
        currentVoice={selectedVoiceSnapshot || currentVoice}
        voices={voices}
        voicesLoading={voicesLoading}
        favoriteVoices={favoriteVoicesList}
        recommendedVoices={recommendedVoicesList}
        favoriteVoiceIds={favoriteVoiceIds}
        playingVoiceId={playingVoiceId}
        open={dropdownOpen}
        onOpenChange={setDropdownOpen}
        onVoiceSelect={handleVoiceSelect}
        onVoicePick={setSelectedVoiceSnapshot}
        onPlayPreview={playVoicePreview}
        onToggleFavorite={handleToggleFavorite}
        onTestVoice={handleTestVoice}
        onOpenModal={() => setModalOpen(true)}
        className={className}
        restrictToVoices={restrictToVoices}
        allowLibraryAdd={allowLibraryAdd}
        hidePlayButton={hidePlayButton}
      />

      {comparisonVoices.length > 0 && (
        <Button
          type="button"
          variant="outline"
          onClick={() => setComparisonPanelOpen(true)}
          className="px-3 h-12 flex-shrink-0 relative"
          title="Compare voices"
        >
          <GitCompare className="h-4 w-4" />
          <Badge className="absolute -top-2 -right-2 h-5 w-5 p-0 flex items-center justify-center text-xs">
            {comparisonVoices.length}
          </Badge>
        </Button>
      )}

      <VoiceBrowserModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        voices={voices}
        voicesLoading={voicesLoading}
        voicesTotalCount={voicesTotalCount}
        voicesHasMore={voicesHasMore}
        onLoadMoreVoices={onLoadMoreVoices}
        favoriteVoices={favoriteVoicesList}
        recommendedVoices={recommendedVoicesList}
        allVoices={accumulatedAllVoices}
        allVoicesTotalCount={allVoicesData?.totalCount || 0}
        allVoicesHasMore={allVoicesData?.hasMore || false}
        allVoicesLoading={allVoicesLoading}
        favoriteVoiceIds={favoriteVoiceIds}
        playingVoiceId={playingVoiceId}
        selectedVoiceId={value}
        filterOptions={filterOptions}
        onVoiceSelect={handleVoiceSelect}
        onVoicePick={setSelectedVoiceSnapshot}
        onPlayPreview={playVoicePreview}
        onToggleFavorite={handleToggleFavorite}
        onTestVoice={handleTestVoice}
        onLoadMoreAllVoices={() => setAllVoicesPage(p => p + 1)}
        onAllVoicesFiltersChange={(filters) => {
          setAllVoicesFilters(filters);
          setAllVoicesPage(0);
        }}
        onAddVoice={handleAddVoice}
        allowLibraryAdd={allowLibraryAdd}
        hideAllVoicesTab={restrictToVoices}
      />

      <VoiceQuickTestSheet
        open={testSheetOpen}
        onOpenChange={setTestSheetOpen}
        voice={testingVoice}
        defaultText={defaultTestText}
        onSelectVoice={handleVoiceSelect}
        showSelectButton={true}
      />

      <VoiceComparisonPanel
        open={comparisonPanelOpen}
        onOpenChange={setComparisonPanelOpen}
        voices={comparisonVoices}
        onSelectVoice={handleVoiceSelect}
        onRemoveVoice={handleRemoveFromComparison}
        defaultText={defaultTestText}
      />
    </>
  );
};
